import React from 'react';
import { FiPlus, FiUser, FiTrash2, FiEdit2, FiExternalLink, FiStar } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const CharacterList = ({ 
  characters, 
  selectedCharacterId, 
  onSelectCharacter, 
  onCreateClick,
  onDeleteCharacter,
  onEditCharacter,
  onAvatarClick
}) => {
  const navigate = useNavigate();

  // Limit to top 10 characters
  const topCharacters = characters.slice(0, 10);

  // Sort characters: public/system ones first, so the default companion remains on top
  const sortedCharacters = [...topCharacters].sort((a, b) => {
    if (a.isPublic && !b.isPublic) return -1;
    if (!a.isPublic && b.isPublic) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="flex-1 overflow-y-auto p-2 space-y-1">
      <h3 className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        Characters ({characters.length})
      </h3>

      {sortedCharacters.map((char) => {
        // Assume read-only if it's a system character or global character.
        const isSystem = char.userId === 'system' || char.isPublic === true;
        const characterType = isSystem ? 'predefined' : 'custom';
        
        return (
          <div key={char.characterId} className="relative group">
            <button
              onClick={() => onSelectCharacter(char.characterId)}
              className={`w-full text-left pl-3 pr-4 py-3 rounded-lg flex items-center gap-3 transition-colors ${
                  selectedCharacterId === char.characterId 
                  ? 'bg-bela-100 text-bela-900' 
                  : 'text-gray-700 hover:bg-gray-300'
              }`}
            >
              <div 
                className="relative w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 border-2 border-white shadow-sm flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onAvatarClick) onAvatarClick(char.characterId);
                }}
              >
                {char.photoUrl ? (
                  <img src={char.photoUrl.startsWith('/') ? `${process.env.PUBLIC_URL}${char.photoUrl}` : char.photoUrl} alt={char.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <FiUser size={20} />
                )}
                {isSystem && (
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                )}
              </div>
              <div className="truncate flex-1">
                <div className="font-medium truncate flex items-center gap-1">
                  {char.name}
                  {!isSystem && <FiStar size={11} className="text-blue-400 flex-shrink-0" title="Custom character" />}
                </div>
                <div className="text-xs text-gray-500 truncate">{char.occupation || (isSystem ? 'Your AI Companion' : 'Custom Character')}</div>
              </div>
            </button>
            
            {/* Action Buttons (Only for non-system characters) */}
            {!isSystem && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditCharacter(char);
                  }}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-gray-200 rounded-full"
                  title="Edit Character"
                >
                  <FiEdit2 size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteCharacter(char.characterId);
                  }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-gray-200 rounded-full"
                  title="Delete Character"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* View All Link */}
      {characters.length > 10 && (
        <button
          onClick={() => navigate('/characters')}
          className="w-full mt-2 flex items-center justify-center gap-2 p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors text-sm font-medium"
        >
          <FiExternalLink size={16} />
          View All Characters ({characters.length})
        </button>
      )}

      {/* Create New Button */}
      <button
        onClick={onCreateClick}
        className="w-full mt-2 flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-bela-500 hover:text-bela-600 transition-colors"
      >
        <FiPlus size={18} />
        <span className="font-medium text-sm">Create Character</span>
      </button>
    </div>
  );
};

export default CharacterList;
